export const guildAdminEn = {
  management: {
    title: 'Guild settings',
    mobileDescription: 'Configure Discord automation, member access, and the shared planner library.',
    desktopDescription: 'Keep Discord automation, access, and shared planner data in sync from one workspace.',
    sectionsLabel: 'Guild settings sections',
    discord: 'Discord controls',
    discordDescription: 'Publish raids automatically and define the language and roles that manage or access this guild.',
    members: 'Member access',
    membersDescription: 'Review requested access and keep the officer roster aligned with Discord.',
    library: 'Tags and builds',
    libraryDescription: 'Maintain the shared labels and build library used across the guild planner.',
  },
  language: {
    title: 'Guild language',
    description: 'Sets the automatic language for guild members and messages sent to this Discord server.',
    label: 'Language',
    english: 'English',
    russian: 'Russian',
    save: 'Save guild language',
    saving: 'Saving…',
    saved: 'Guild language saved.',
    chooseGuild: 'Choose a guild before changing its language.',
    failed: 'Unable to save the guild language.',
  },
  accessRole: {
    title: 'Guild access role',
    description: 'When set, only Discord members with this role can view this guild. Saving checks that the bot can assign it.',
    roleId: 'Discord role ID',
    placeholder: 'Leave blank to allow every guild member',
    publicStatus: 'Every current Discord guild member can view this guild.',
    restrictedStatus: 'Members without this role can request access from a guild owner.',
    roleName: (name: string) => `Discord role: ${name}`,
    save: 'Save access role',
    saving: 'Saving…',
    invalid: 'Enter a valid Discord role ID, or leave it blank to allow every guild member.',
    chooseGuild: 'Choose a guild before changing its access role.',
    failed: 'Unable to save the access role.',
  },
  officerRole: {
    title: 'Officer role',
    description: 'Members holding this Discord role receive officer permissions after the bot syncs their membership.',
    roleId: 'Discord role ID',
    placeholder: 'Leave blank to disable role-based officer access',
    disabledStatus: 'No Discord role currently grants officer access.',
    enabledStatus: 'The bot applies this role on its next roster sync and on future role updates.',
    roleName: (name: string) => `Discord role: ${name}`,
    save: 'Save officer role',
    saving: 'Saving…',
    invalid: 'Enter a valid Discord role ID, or leave it blank to disable role-based access.',
    chooseGuild: 'Choose a guild before changing its officer role.',
    failed: 'Unable to save the officer role.',
  },
  publication: {
    title: 'Raid publication',
    description: 'Publish scheduled raids in one Discord text channel while they fall inside the rolling window.',
    channelId: 'Discord text channel ID',
    channelPlaceholder: 'Leave blank to disable automatic publication',
    daysAhead: 'Publish raids this many days ahead',
    completedMessageRetentionHours: 'Keep completed raid messages for this many hours',
    completedMessageRetentionHint: 'Completed cards are deleted after the raid ends; 48 hours is the default.',
    disabledStatus: 'Automatic publication is disabled.',
    enabledStatus: (days: string) => `Raids scheduled within the next ${days} days will be published.`,
    channelName: (name: string) => `Discord channel: #${name}`,
    save: 'Save publication settings',
    saving: 'Saving…',
    verifyFailed: (reason: string) => `Unable to verify this Discord channel: ${reason}`,
    invalidChannel: 'Enter a valid Discord channel ID, or leave it blank to disable publishing.',
    invalidDays: 'Choose a whole number from 1 to 30 days.',
    invalidCompletedMessageRetentionHours: 'Choose a whole number from 1 to 720 hours.',
    chooseGuild: 'Choose a guild before changing its publication settings.',
    failed: 'Unable to save the publication settings.',
  },
  notifications: {
    title: 'Forest voice notifications',
    description: 'Play forest notifications in Discord and TeamSpeak 6 at the same time.',
    fleetAvailability: (configured: number, available: number, channels: number) => `Discord: ${configured} bot account${configured === 1 ? '' : 's'} configured · ${available} available in this guild · up to ${channels} simultaneous channel${channels === 1 ? '' : 's'}`,
    accountStatus: (index: number, role: 'coordinator' | 'worker', ready: boolean, guildPresent: boolean, requiresOauthCodeGrant: boolean) => {
      const label = `${role === 'coordinator' ? 'Coordinator' : 'Worker'} #${index}`
      if (!guildPresent && requiresOauthCodeGrant) {
        return `${label}: direct invite unavailable because Require OAuth2 Code Grant is enabled. Disable it in the Discord application’s Bot settings; this status updates automatically.`
      }
      if (!ready) return `${label}: gateway unavailable`
      return guildPresent ? `${label}: ready in this guild` : `${label}: connected, but not in this guild`
    },
    inviteBot: (index: number, role: 'coordinator' | 'worker') => `Invite ${role} #${index} to this server ↗`,
    capacityWarning: (saved: number, draft: number, concurrent: number) => `Discord: ${saved === draft ? `${saved} channels are saved.` : `${saved} channels are saved; this draft has ${draft}. Save notification settings to persist the draft.`} Only ${concurrent} can receive each notification until more bot accounts are available.`,
    enabled: 'Enable voice notifications',
    automatic: 'Start automatically for scheduled raids',
    interval: 'Forest interval (seconds)',
    offsets: 'Notification lead times (seconds)',
    offsetsPlaceholder: '30, 10',
    mode: 'Playback mode',
    audio: 'Audio',
    soundboard: 'Discord soundboard',
    teamspeakChannels: 'TeamSpeak 6 channels',
    teamspeakSelected: (count: number, limit: number) => `Selected: ${count} / ${limit}`,
    teamspeakRefresh: 'Refresh channels',
    teamspeakRetry: 'Retry loading channels',
    teamspeakLoading: 'Loading TeamSpeak channels…',
    teamspeakEmpty: 'No TeamSpeak channels are available.',
    teamspeakLoadFailed: 'Could not load TeamSpeak channels.',
    teamspeakMissingChannel: (id: string) => `Saved channel #${id} (unavailable)`,
    teamspeakHint: 'Select the channels that should receive notifications. One bot broadcasts audio to all selected TeamSpeak channels alongside Discord notifications.',
    teamspeakUnavailable: 'TeamSpeak is not configured for this guild. Ask the server administrator to connect the audio bot. Saved selections are retained.',
    teamspeakAudioHint: 'Soundboard selection applies to Discord. TeamSpeak plays the configured notification audio.',
    voiceChannels: 'Discord voice channel IDs',
    voiceChannelId: (position: number) => `Discord voice channel ${position} ID`,
    addVoiceChannel: 'Add voice channel',
    removeVoiceChannel: (position: number) => `Remove voice channel ${position}`,
    voiceChannelName: (name: string) => `Discord voice channel: ${name}`,
    foreignVoiceChannelInviteHelp: 'This voice channel belongs to another Discord server. Add each missing wwm-notify account to that server before testing or saving this destination.',
    inviteBotToForeignVoiceGuild: (index: number, role: 'coordinator' | 'worker') => `Invite ${role} #${index} to that server ↗`,
    foreignVoiceChannelCodeGrant: (index: number, role: 'coordinator' | 'worker') => `A direct invite for ${role} #${index} is unavailable because Require OAuth2 Code Grant is enabled. Disable it in that Discord application’s Bot settings; the invite updates automatically.`,
    validation: {
      voice_channels_required: 'Add a Discord voice channel or select a TeamSpeak channel.',
      voice_channels_limit: 'Use no more than 16 voice channels.',
      voice_channel_invalid: 'Voice channel IDs must be 17–20 digit Discord snowflakes.',
      voice_channel_duplicate: 'Each voice channel ID may appear only once.',
      teamspeak_channels_limit: 'Use no more than 16 TeamSpeak channels.',
      teamspeak_channel_invalid: 'TeamSpeak IDs must be positive whole decimal numbers up to 18446744073709551615, without leading zeroes.',
      teamspeak_channel_duplicate: 'Each TeamSpeak channel ID may appear only once.',
      offsets_required: 'Add at least one notification lead time.',
      offset_invalid: 'Lead times must be whole seconds from 0 to 3600.',
      offset_duplicate: 'Each notification lead time may appear only once.',
    },
    soundId: 'Soundboard sound ID',
    sourceGuildId: 'Soundboard source guild ID',
    save: 'Save notification settings',
    saving: 'Saving…',
    test: 'Test notification',
    testing: 'Testing…',
    tested: (report: string) => `Test notification: ${report}`,
    loading: 'Loading notification settings…',
    retry: 'Retry loading settings',
    saved: 'Notification settings saved.',
    invalidInterval: 'Choose a whole-number interval from 60 to 3600 seconds.',
    invalidSoundId: 'Enter a valid Discord soundboard sound ID.',
    invalidSourceGuildId: 'Enter a valid Discord source guild ID.',
    chooseGuild: 'Choose a guild before changing notification settings.',
  },
  notificationTimers: {
    title: 'Notification timers',
    description: 'Live guild timing, forest cadence, and manual wwm-notify controls in one place.',
    setupDescription: 'Add the wwm-notify coordinator bot to this guild before managing notification timers.',
    setupAdd: 'Add the wwm-notify bot to this Discord server.',
    setupReturn: 'Return here after Discord confirms the invite; the timer controls will appear automatically.',
    inviteBot: 'Add wwm-notify bot to this guild ↗',
    inviteUnavailable: 'A direct invite link is unavailable. Disable Require OAuth2 Code Grant for the wwm-notify application; this status updates automatically.',
    liveSync: 'Live sync',
    liveConnecting: 'Connecting',
    liveIssue: 'Sync issue',
    gameTimer: 'Game timer (optional)',
    gameTimerPlaceholder: 'Blank, -3:20, or 23:25',
    gameTimerHelp: 'The game lasts 30 minutes. Use -MM:SS until start or MM:SS until game end.',
    gameTimerOptionalHelp: 'Leave blank to start the game now.',
    modifyTimerHelp: 'Leave the game timer blank to keep the current schedule.',
    fixedDuration: '30 minute game',
    quickGuide: 'Game clock quick guide',
    blankValue: 'Blank',
    blankExample: 'The game starts now.',
    beforeStartExample: '-3:20 means the game starts in 3 minutes 20 seconds.',
    runningExample: '23:25 means the game ends in 23 minutes 25 seconds.',
    name: 'Timer name (optional)',
    namePlaceholder: 'Forest rotation',
    nameOptionalHelp: 'Unnamed timers appear as Forest.',
    unnamed: 'Forest',
    addTitle: 'Add a timer',
    add: 'Add timer',
    adding: 'Adding…',
    listTitle: 'Current guild timers',
    listLabel: 'Current guild notification timers',
    loading: 'Loading notification timers…',
    retry: 'Retry loading timers',
    waitingForLiveSync: 'The live subscription will retry automatically.',
    empty: 'No timers are active or scheduled for this guild.',
    countdownHelp: 'Countdowns update locally every second from the subscribed schedule.',
    nextNotification: 'Next alert',
    nextForest: 'Next forest',
    gameEndsIn: 'Game ends',
    notScheduled: '—',
    countdown: (clock: string) => `in ${clock}`,
    scheduleRange: (start: string, end: string) => `${start} → ${end}`,
    sourceLabel: 'Source',
    statusLabel: 'Status',
    actionsLabel: 'Timer actions',
    startsAt: 'Starts',
    endsAt: 'Ends',
    duration: 'Duration',
    durationMinutes: (minutes: number) => `${minutes} min`,
    source: {
      manual: 'Manual',
      external: 'Planner',
    },
    status: {
      scheduled: 'Scheduled',
      running: 'Running',
      finished: 'Finished',
      stopped: 'Stopped',
    },
    readOnly: 'Managed by the planner',
    plannerSaved: 'Planner timer updated. wwm-notify will synchronize it.',
    plannerStopped: 'Planner timer stopped. Publish the raid again from Calendar to restart it.',
    modify: 'Modify',
    start: 'Start',
    restart: 'Restart',
    stop: 'Stop',
    stopping: 'Stopping…',
    remove: 'Remove',
    removing: 'Removing…',
    modifyAria: (name: string) => `Modify timer ${name}`,
    startAria: (name: string, restart: boolean) => `${restart ? 'Restart' : 'Start'} timer ${name}`,
    stopAria: (name: string) => `Stop timer ${name}`,
    removeAria: (name: string) => `Remove timer ${name}`,
    editTitle: (name: string) => `Modify ${name}`,
    startTitle: (name: string) => `${name}: start or restart`,
    saveChanges: 'Save changes',
    starting: 'Starting…',
    saving: 'Saving…',
    cancel: 'Cancel',
    channelTitle: 'Active timer status channel',
    channelDescription: 'wwm-notify keeps one Discord message in this text channel updated with active timers. When none are active, it shows “no timers”.',
    channelId: 'Discord text channel ID',
    channelPlaceholder: 'Leave blank to disable the status message',
    channelName: (name: string) => `Discord text channel: #${name}`,
    channelDisabled: 'No timer status channel is configured.',
    channelMessage: (messageId: string) => `Status message ID: ${messageId}`,
    channelConnected: 'Message connected',
    saveChannel: 'Save status channel',
    savingChannel: 'Saving…',
    clearChannel: 'Clear channel',
    validation: {
      game_timer_invalid: 'Enter MM:SS with an optional + or - sign and seconds from 00 to 59.',
      game_timer_finished: '0:00 means the game has already ended.',
      game_timer_exceeds_duration: 'Time remaining cannot exceed 30:00.',
      channelInvalid: 'Enter a valid 17–20 digit Discord channel ID.',
    },
    saved: 'Notification timer updated.',
    removed: 'Notification timer removed.',
    channelSaved: 'Timer status channel updated.',
    chooseGuild: 'Choose a guild before changing notification timers.',
    failed: 'Unable to update notification timers.',
  },
  requests: {
    title: 'Access requests',
    description: "Granting a request asks the bot to add this guild's configured access role.",
    loading: 'Loading access requests…',
    configureFirst: 'Set an access role before reviewing requests.',
    empty: 'No pending access requests.',
    listLabel: 'Pending access requests',
    requested: (date: string) => `Requested ${date}`,
    grantLabel: (name: string) => `Grant ${name} the configured access role`,
    granting: 'Granting…',
    grant: 'Grant access role',
    failed: 'Unable to grant this access request.',
  },
  onboarding: {
    accessTitle: 'Guild access required',
    accessDescription: 'Your Discord account belongs to these guilds, but a guild access role is required before their planner can be viewed.',
    pending: 'Access request pending',
    roleRequired: 'Discord role required to view',
    requestPending: 'Request pending',
    requesting: 'Requesting…',
    request: 'Request access',
    requestFailed: 'Unable to request access to this guild.',
    loginTitle: 'Sign in with Discord',
    loginDescription: 'Sign in with Discord to see your guilds and add the bot to a server.',
    login: 'Login with Discord',
    emptyTitle: 'No guilds yet',
    emptyDescription: "You aren't in any Discord server that has this bot installed. Add the bot to your Discord server to start planning raids.",
    addBotFromSelector: 'Use “Add the bot to your Discord server” in the guild switcher above.',
    refresh: 'After the bot joins, refresh this page and your server will appear in the dropdown above.',
  },
  roster: {
    title: 'Manage officers',
    ownerOnly: 'Only the Discord owner of this guild can change these.',
    loading: 'Loading roster…',
    empty: 'No roster synced yet. Ask the bot to run /sync_members in Discord.',
    owner: 'Owner',
    officer: 'Officer',
    ownerAria: (name: string) => `Officer status for ${name}. Owner is always an officer; read-only.`,
    toggleAria: (name: string) => `Toggle officer status for ${name}`,
    ownerTitle: 'The owner is always an officer; this row is read-only by design.',
    toggleTitle: 'Toggle officer status',
    failed: 'Failed to update officer status.',
  },
  builds: {
    title: 'Guild builds',
    description: "Curate the shared guild build library and who it's assigned to.",
    counts: (builds: number, players: number) => `${builds} ${builds === 1 ? 'build' : 'builds'} · ${players} ${players === 1 ? 'player' : 'players'}`,
    viewLabel: 'Guild builds view',
    library: 'Library',
    assignments: 'Assignments',
    listLabel: 'Guild builds',
    buildNumber: (number: number) => `Build #${number}`,
    assignedToNone: 'Assigned to: —',
    assignedTo: (names: string) => `Assigned to: ${names}`,
    editFirst: 'Edit first',
    editSecond: 'Edit second',
    remove: 'Remove',
    empty: 'No guild builds yet. Create one to get started.',
    create: '+ Create guild build',
    buildCount: (count: number) => `${count} ${count === 1 ? 'build' : 'builds'}`,
    unassignAria: (number: number, name: string) => `Unassign build #${number} from ${name}`,
    noneAssigned: 'No guild builds assigned.',
    assignAria: (name: string) => `Assign a guild build to ${name}`,
    assign: '+ Assign',
    availableAria: (name: string) => `Available builds for ${name}`,
    noAvailable: 'No unassigned builds available.',
    noPlayers: 'No players on the board yet.',
    noWeapons: 'No weapons selected',
    chooseFirst: 'Choose first guild build weapon',
    chooseSecond: 'Choose second guild build weapon',
    pickerOpen: 'Picker already open',
    createTitle: 'Create a new guild build',
    removeConfirm: (number: number, summary: string) => `Remove guild build #${number} (${summary})? This unassigns every player currently using it.`,
    selectPlayersAria: (number: number) => `Select players for guild build #${number}`,
    playersAria: (number: number) => `Players for guild build #${number}`,
    assignPlayers: 'Assign players',
    editPlayers: 'Edit players',
    assigned: 'Assigned',
    assignOne: 'Assign',
  },
} as const
